import { useState, useEffect } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { verifyResetToken, resetPassword } from '../lib/api';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Alert, AlertDescription } from '../components/ui/alert';
import { 
    Loader2, 
    Lock, 
    CheckCircle,
    XCircle,
    Eye,
    EyeOff,
    Ticket
} from 'lucide-react';

export default function ResetPasswordPage() {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const token = searchParams.get('token');
    
    const [loading, setLoading] = useState(true);
    const [tokenValid, setTokenValid] = useState(false);
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [success, setSuccess] = useState(false);
    const [error, setError] = useState('');

    useEffect(() => {
        if (token) {
            verifyToken();
        } else {
            setLoading(false);
        }
    }, [token]);

    const verifyToken = async () => {
        try {
            const response = await verifyResetToken(token);
            setTokenValid(true);
            setEmail(response.data.email);
        } catch (err) {
            setTokenValid(false);
            setError(err.response?.data?.detail || 'Token non valido o scaduto');
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');

        if (password !== confirmPassword) {
            setError('Le password non coincidono');
            return;
        }

        if (password.length < 6) {
            setError('La password deve essere di almeno 6 caratteri');
            return;
        }

        setSubmitting(true);
        try {
            await resetPassword(token, password);
            setSuccess(true);
        } catch (err) {
            setError(err.response?.data?.detail || 'Errore durante il reset della password');
        } finally {
            setSubmitting(false);
        }
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white flex items-center justify-center">
                <Loader2 className="w-8 h-8 animate-spin text-indigo-500" />
            </div>
        );
    }

    if (!token || !tokenValid) {
        return (
            <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white flex items-center justify-center p-4">
                <Card className="w-full max-w-md border-slate-200">
                    <CardContent className="pt-6">
                        <div className="text-center">
                            <div className="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-4">
                                <XCircle className="w-8 h-8 text-red-600" />
                            </div>
                            <h2 className="text-2xl font-bold text-slate-900 mb-2">Link Non Valido</h2>
                            <p className="text-slate-600 mb-6">
                                {error || 'Il link di reset password non è valido o è scaduto.'}
                            </p>
                            <Link to="/forgot-password">
                                <Button className="w-full bg-indigo-500 hover:bg-indigo-600">
                                    Richiedi Nuovo Link
                                </Button>
                            </Link>
                        </div>
                    </CardContent>
                </Card>
            </div>
        );
    }

    if (success) {
        return (
            <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white flex items-center justify-center p-4">
                <Card className="w-full max-w-md border-slate-200">
                    <CardContent className="pt-6">
                        <div className="text-center">
                            <div className="w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-4">
                                <CheckCircle className="w-8 h-8 text-emerald-600" />
                            </div>
                            <h2 className="text-2xl font-bold text-slate-900 mb-2">Password Reimpostata!</h2>
                            <p className="text-slate-600 mb-6">
                                La tua password è stata aggiornata con successo. 
                                Ora puoi accedere con la nuova password.
                            </p>
                            <Link to="/login">
                                <Button className="w-full bg-indigo-500 hover:bg-indigo-600">
                                    Vai al Login
                                </Button>
                            </Link>
                        </div>
                    </CardContent>
                </Card>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white flex items-center justify-center p-4">
            <Card className="w-full max-w-md border-slate-200">
                <CardHeader className="text-center pb-2">
                    <div className="w-14 h-14 rounded-xl bg-indigo-500 flex items-center justify-center mx-auto mb-4">
                        <Ticket className="w-7 h-7 text-white" />
                    </div>
                    <CardTitle className="text-2xl">Nuova Password</CardTitle>
                    <CardDescription>
                        Inserisci la nuova password per <strong>{email}</strong>
                    </CardDescription>
                </CardHeader>
                <CardContent>
                    <form onSubmit={handleSubmit} className="space-y-4">
                        {error && (
                            <Alert variant="destructive">
                                <AlertDescription>{error}</AlertDescription>
                            </Alert>
                        )}
                        
                        <div className="space-y-2">
                            <Label htmlFor="password">Nuova Password</Label>
                            <div className="relative">
                                <Input
                                    id="password"
                                    type={showPassword ? "text" : "password"}
                                    placeholder="Almeno 6 caratteri"
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    required
                                    data-testid="new-password-input"
                                />
                                <Button
                                    type="button"
                                    variant="ghost"
                                    size="sm"
                                    className="absolute right-0 top-0 h-full px-3"
                                    onClick={() => setShowPassword(!showPassword)}
                                >
                                    {showPassword ? (
                                        <EyeOff className="w-4 h-4" />
                                    ) : (
                                        <Eye className="w-4 h-4" />
                                    )}
                                </Button>
                            </div>
                        </div>

                        <div className="space-y-2">
                            <Label htmlFor="confirmPassword">Conferma Password</Label>
                            <Input
                                id="confirmPassword"
                                type={showPassword ? "text" : "password"}
                                placeholder="Ripeti la password"
                                value={confirmPassword}
                                onChange={(e) => setConfirmPassword(e.target.value)}
                                required
                                data-testid="confirm-password-input"
                            />
                        </div>

                        <Button 
                            type="submit" 
                            className="w-full bg-indigo-500 hover:bg-indigo-600" 
                            disabled={submitting || !password || !confirmPassword}
                            data-testid="reset-submit-btn"
                        >
                            {submitting ? (
                                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            ) : (
                                <Lock className="w-4 h-4 mr-2" />
                            )}
                            Reimposta Password
                        </Button>
                    </form>
                </CardContent>
            </Card>
        </div>
    );
}
